import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Paperclip, Loader2, PackagePlus, FileCheck2, ArrowRight } from 'lucide-react';

export const BubbleActionCard = ({ action }) => (
  <div className="mt-2 rounded-md border bg-background p-2 text-xs" data-testid={`ai-bubble-duti-card-${action.action_id}`}>
    <p className="font-medium">Waiting for approval</p>
    <p className="text-muted-foreground mt-0.5">{action.summary}</p>
    <Link to="/duti-ai" className="inline-flex items-center gap-1 mt-1.5 text-primary hover:underline" data-testid={`ai-bubble-duti-open-queue-${action.action_id}`}>
      Open approval queue <ArrowRight className="h-3 w-3" />
    </Link>
  </div>
);

export const ReorderDraftButtons = ({ candidates, onDrafted }) => {
  const [busy, setBusy] = useState(null);
  const draft = async (c) => {
    setBusy(c.product_id);
    try {
      const { data } = await api.post('/duti/actions/purchase-order', {
        product_id: c.product_id, supplier_name: c.supplier_name, quantity: c.suggested_quantity,
      });
      toast.success('Purchase order drafted for approval');
      onDrafted(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not draft the purchase order');
    } finally {
      setBusy(null);
    }
  };
  return (
    <div className="mt-2 space-y-1.5">
      {candidates.map((c) => (
        <Button key={c.product_id} size="sm" variant="outline" className="h-auto w-full justify-start py-1.5 text-xs whitespace-normal text-left"
          onClick={() => draft(c)} disabled={!!busy} data-testid={`ai-bubble-draft-po-btn-${c.product_id}`}>
          {busy === c.product_id ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5 shrink-0" /> : <PackagePlus className="h-3.5 w-3.5 mr-1.5 shrink-0" />}
          Draft PO: {c.suggested_quantity} × {c.product_name} from {c.supplier_name}
        </Button>
      ))}
    </div>
  );
};

export const DutiAttach = ({ onResult, disabled }) => {
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState('product');
  const [po, setPo] = useState('');
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const upload = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    if (kind === 'invoice' && files.length > 10) {
      onResult({ ok: false, message: 'Please upload up to 10 invoice pages at a time.' });
      return;
    }
    setBusy(true);
    const form = new FormData();
    form.append('kind', kind);
    form.append('po_reference', po);
    files.forEach((f) => form.append('files', f));
    try {
      const { data } = await api.post('/duti/actions/from-document', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      const label = kind === 'product' ? 'product photo' : `invoice (${data.pages} page${data.pages > 1 ? 's' : ''}, ${data.lines} line${data.lines === 1 ? '' : 's'})`;
      onResult({ ok: true, action: data, label });
      setOpen(false);
    } catch (e) {
      onResult({ ok: false, message: e.response?.data?.detail || 'Could not read that file' });
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const pick = (k) => { setKind(k); if (k === 'product') setTimeout(() => fileRef.current?.click(), 0); };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button size="icon" variant="ghost" className="h-9 w-9 shrink-0" disabled={disabled || busy} aria-label="Attach a photo or document" data-testid="ai-bubble-attach-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
        </Button>
      </PopoverTrigger>
      <PopoverContent side="top" align="start" className="w-64 space-y-2 p-3 z-50" data-testid="ai-bubble-attach-menu">
        <Button variant="outline" size="sm" className="w-full justify-start" onClick={() => pick('product')} data-testid="ai-bubble-attach-product">
          <PackagePlus className="h-4 w-4 mr-2" />New product from photo
        </Button>
        <Button variant={kind === 'invoice' ? 'secondary' : 'outline'} size="sm" className="w-full justify-start" onClick={() => setKind('invoice')} data-testid="ai-bubble-attach-invoice">
          <FileCheck2 className="h-4 w-4 mr-2" />Match an invoice to a PO
        </Button>
        {kind === 'invoice' && (
          <div className="space-y-2">
            <Input value={po} onChange={(e) => setPo(e.target.value)} placeholder="PO number, e.g. PO-2026…" className="h-8 text-xs" data-testid="ai-bubble-attach-po-input" />
            <Button size="sm" className="w-full" disabled={!po.trim()} onClick={() => fileRef.current?.click()} data-testid="ai-bubble-attach-invoice-choose">
              Choose invoice pages (up to 10)
            </Button>
          </div>
        )}
        <input ref={fileRef} type="file" accept="image/*,application/pdf" multiple={kind === 'invoice'} className="hidden" onChange={(e) => upload(e.target.files)} data-testid="ai-bubble-attach-file-input" />
      </PopoverContent>
    </Popover>
  );
};
